import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Badge                                                             */
/* ------------------------------------------------------------------ */

export type BadgeVariant = "default" | "success" | "warning" | "error" | "info";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** Show a small filled dot indicator before the text. */
  dot?: boolean;
}

const css = /* css */ `
  .sf-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: var(--sf-font-sans);
    font-size: var(--sf-text-xs);
    font-weight: var(--sf-weight-medium);
    line-height: 1;
    padding: 3px 8px;
    border-radius: var(--sf-radius-full);
    white-space: nowrap;
    vertical-align: middle;
  }

  .sf-badge__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  /* Variant: default (neutral) */
  .sf-badge--default {
    background: var(--sf-gray-100);
    color: var(--sf-text-secondary);
  }
  .sf-badge--default .sf-badge__dot { background: var(--sf-gray-500); }

  /* Variant: success */
  .sf-badge--success {
    background: var(--sf-success-light);
    color: var(--sf-success);
  }
  .sf-badge--success .sf-badge__dot { background: var(--sf-success); }

  /* Variant: warning */
  .sf-badge--warning {
    background: var(--sf-warning-light);
    color: var(--sf-warning);
  }
  .sf-badge--warning .sf-badge__dot { background: var(--sf-warning); }

  /* Variant: error */
  .sf-badge--error {
    background: var(--sf-error-light);
    color: var(--sf-error);
  }
  .sf-badge--error .sf-badge__dot { background: var(--sf-error); }

  /* Variant: info */
  .sf-badge--info {
    background: var(--sf-info-light);
    color: var(--sf-info);
  }
  .sf-badge--info .sf-badge__dot { background: var(--sf-info); }
`;

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = "default", dot = false, children, className, ...rest }, ref) => {
    injectStyles("sf-badge", css);

    const classes = [
      "sf-badge",
      `sf-badge--${variant}`,
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <span ref={ref} className={classes} {...rest}>
        {dot && <span className="sf-badge__dot" aria-hidden="true" />}
        {children}
      </span>
    );
  },
);

Badge.displayName = "Badge";
export { Badge };
